import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import logo from "../assets/logo-bg.png";

export default function LoginPage() {
  const API_URL = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();

  const [role, setRole] = useState("Tradesman");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [agree, setAgree] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [infoMessage, setInfoMessage] = useState("");

  const handleLogin = async () => {
    let newErrors = {};
    if (!username.trim()) newErrors.username = "Username is required";
    if (!password.trim()) newErrors.password = "Password is required";
    if (!agree) newErrors.agree = "You must agree to the disclaimer";
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    const credentials = {
      username,
      password,
      role: role.toUpperCase(),
    };

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(credentials),
      });

      const data = await res.json();

      if (res.ok && data.token) {
        localStorage.setItem("token", data.token);
        localStorage.setItem("refreshToken", data.refreshToken);
        localStorage.setItem("user", JSON.stringify(data));

        if (data.role === "MANAGER") navigate("/manager-dashboard");
        else navigate("/tradesman-dashboard");
      } else {
        setErrors({ general: data.message || "Invalid credentials" });
      }
    } catch (err) {
      console.error("Error:", err);
      setErrors({ general: "Server unreachable. Try again later." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const message = sessionStorage.getItem("redirectMessage");
    if (message) {
      setInfoMessage(message);
      sessionStorage.removeItem("redirectMessage"); // Nettoyage
    }
  }, []);

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
        {/* Logo */}
        <div className="flex justify-center mb-2">
          <img
            src={logo}
            alt="Tradie Time Logo"
            className="h-[150px] w-auto object-contain"
          />
        </div>
        {infoMessage && (
          <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-800 p-3 rounded mb-4 text-sm">
            {infoMessage}
          </div>
        )}

        <h2 className="text-1xl font-bold text-blue-900 mb-6 text-center">
          Welcome, please sign in
        </h2>

        <div className="space-y-4 mb-6">
          {/* Select Role */}
          <div>
            <label className="block text-gray-700 text-sm font-semibold mb-2">
              Select Role
            </label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-yellow-500 focus:outline-none text-gray-900"
            >
              <option>Tradesman</option>
              <option>Manager</option>
            </select>
          </div>

          {/* Username */}
          <div>
            <label className="block text-gray-700 text-sm font-semibold mb-2">
              Username
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter username"
              className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none text-gray-900 ${
                errors.username
                  ? "border-red-500"
                  : "border-gray-200 focus:border-yellow-500"
              }`}
            />
            {errors.username && (
              <p className="text-red-500 text-sm mt-1">{errors.username}</p>
            )}
          </div>

          {/* Password */}
          <div>
            <label className="block text-gray-700 text-sm font-semibold mb-2">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none text-gray-900 ${
                errors.password
                  ? "border-red-500"
                  : "border-gray-200 focus:border-yellow-500"
              }`}
            />
            {errors.password && (
              <p className="text-red-500 text-sm mt-1">{errors.password}</p>
            )}
          </div>

          {/* Disclaimer */}
          <div className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={agree}
              onChange={(e) => setAgree(e.target.checked)}
              className="mt-1"
            />
            <label className="text-sm text-gray-600">
              I agree to the disclaimer
            </label>
          </div>
          {errors.agree && (
            <p className="text-red-500 text-sm">{errors.agree}</p>
          )}

          <div className="border-2 border-blue-900 bg-gray-50 rounded-lg p-4 mb-4 shadow-inner">
            <p className="text-xs text-gray-600 leading-relaxed">
              By logging in, you confirm that your credentials are provided by
              your company and that your activity may be monitored for
              administrative purposes.
            </p>
          </div>
        </div>

        {errors.general && (
          <p className="text-red-500 text-sm text-center mb-2">
            {errors.general}
          </p>
        )}

        {/* Button */}
        <button
          onClick={handleLogin}
          disabled={loading}
          className={`w-full font-bold py-3 rounded-lg transition-colors mb-4 ${
            loading
              ? "bg-yellow-300 text-white cursor-not-allowed"
              : "bg-yellow-500 text-white hover:bg-yellow-600"
          }`}
        >
          {loading ? "Signing in..." : "Login"}
        </button>

        <div className="text-center">
          <a
            href="mailto:admin@tradietime.co.nz"
            className="text-blue-900 hover:text-yellow-500 text-sm"
          >
            Username/Password forgotten?
          </a>
        </div>
      </div>
    </div>
  );
}
